import Core            from '@/core/Core';
import TenderBid       from '@/domains/TenderBids/TenderBid';
import TenderBidsDAO   from '@/domains/TenderBids/TenderBidsDAO';
import Tender          from '@/domains/Tenders/Tender';
import TendersService  from '@/domains/Tenders/TendersService';
import PartnersService from '@/domains/Partners/PartnersService';
import ContextMenuItem from '@/lib/components/VContextMenu/ContextMenuItem';

class TenderBidsService {
  static UNAVAILABLE_REASONS = {
    NOT_BID_OWNER:              'Ставку может удалить только её владелец',
    IS_SELLER:                  'Продавец не может совершить это действие',
    NO_EXIST_ENTITY_ON_BACKEND: 'Cтавка ещё не создана',
    NO_ACCESS:                  'Нет прав',
  };

  /**
   * Загрузить ставку по ID
   */
  static async getBid(
    filters: Parameters<typeof TenderBidsDAO.getBid>[0]
  ): Promise<any> {
    return await TenderBidsDAO.getBid(filters);
  }

  /**
   * Создать ставку
   */
  static async addBid(tenderBid: TenderBid): Promise<boolean> {
    const errors = tenderBid.validate();

    if (errors.length > 0) {
      Core.showNotification.error(errors.join('\n'));
      return false;
    }

    const response = await TenderBidsDAO.addBid(tenderBid);

    if (response.ok) {
      Core.showNotification.success('Ставка добавлена');
    }

    return response.ok;
  }

  /**
   * Обновить ставку
   */
  static async updateBid(tenderBid: TenderBid): Promise<boolean> {
    const errors = tenderBid.validate();

    if (errors.length > 0) {
      Core.showNotification.error(errors.join('\n'));
      return false;
    }

    const response = await TenderBidsDAO.updateBid(tenderBid);

    if (response.ok) {
      Core.showNotification.success('Ставка обновлена');
    }

    return response.ok;
  }

  /**
   * Удалить ставку
   */
  static async deleteBid(tenderBid: TenderBid): Promise<boolean>;
  static async deleteBid(tenderBidId: number): Promise<boolean>;

  static async deleteBid(tenderBid: TenderBid | number): Promise<boolean> {
    const id = typeof tenderBid === 'number' ? tenderBid : tenderBid.id;
    const response = await TenderBidsDAO.deleteBid(id);

    if (response.ok) {
      Core.showNotification.success('Ставка удалена');
    }

    return response.ok;
  }

  /**
   * Удалить ставку с подтверждением.
   * Возвращает true если удаление произошло — вызывающий сам решает что делать.
   */
  static async deleteBidWithConfirm(tenderBid: TenderBid): Promise<boolean> {
    const confirmed = await Core.confirm('Подтверждаете удаление ставки?');

    if (confirmed) {
      return await this.deleteBid(tenderBid);
    } else {
      return false;
    }
  }

  /**
   * Возвращает причину недоступности сравнения с потребностью
   */
  static getReasonCannotCompareWithDemand(tenderBid: TenderBid | null): string | null {
    if (!tenderBid?.id) { return this.UNAVAILABLE_REASONS.NO_EXIST_ENTITY_ON_BACKEND; }
    if (!Core.currentUser) { return 'Пользователь не авторизован'; }
    if (Core.currentPartner!.isSeller && tenderBid.isMine === false) { return this.UNAVAILABLE_REASONS.NO_ACCESS; }

    return null;
  }

  /**
   * Возвращает причину недоступности просмотра партнёра
   */
  static getReasonCannotShowPartner(): string | null {
    if (!Core.currentUser) { return 'Пользователь не авторизован'; }
    if (Core.currentPartner!.isSeller) { return this.UNAVAILABLE_REASONS.IS_SELLER; }

    return null;
  }

  /**
   * Возвращает причину недоступности удаления ставки
   */
  static getReasonCannotDelete(tenderBid: TenderBid | null): string | null {
    if (tenderBid === null) { return 'Ставка не указана'; }
    if (Core.utils.isNullOrUndefined(tenderBid.id) === true) { return this.UNAVAILABLE_REASONS.NO_EXIST_ENTITY_ON_BACKEND; }
    if (Core.utils.isNullOrUndefined(Core.currentUser) === true) { return 'Пользователь не авторизован'; }
    if (tenderBid.isMine === false) { return this.UNAVAILABLE_REASONS.NOT_BID_OWNER; }
    if (tenderBid?.tender?.isFinished === true) return TendersService.UNAVAILABILITY_REASONS.IS_FINISHED;

    return null;
  }

  /**
   * Причина, по которой ставку нельзя открыть или редактировать.
   * Продавец видит чужие ставки в списке, но работать может только со своей.
   */
  static getReasonCannotEdit(tenderBid: TenderBid): string | null {
    if (Core.currentPartner!.isSeller && tenderBid.isMine === false) {
      return this.UNAVAILABLE_REASONS.NO_ACCESS;
    }

    return null;
  }

  // ============================================================
  // Контекстное меню — ставка
  // ============================================================

  /**
   * Контекстное меню ставки. Заменяет прежний TenderBidContextMenu: пропы
   * disableEdit/disabledHistory приходили туда из грида, но считались от
   * самой ставки — теперь считаются здесь.
   */
  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (tenderBid: TenderBid) => { Core.dialogs.TenderBidCard.show({ tenderBid: tenderBid }); },
        (tenderBid: TenderBid) => this.getReasonCannotEdit(tenderBid),
        false,  // hidden
        true,   // refreshAfter
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'Создать заказ',
        'add_shopping_cart',
        async (tenderBid: TenderBid) => {
          /** Клонируем ставку и тендер: диалог покупки правит их под себя,
           *  а строка грида ссылается на живой тендер, который перечитывается
           *  поллингом. */
          return Core.dialogs.OrderCreateDialogMinimized.showAsPromise({
            showOrderType: true,
            tenderBid:     Core.utils.cloneDeep(tenderBid),
            tender:        Core.utils.cloneDeep(tenderBid.tender),
          });
        },
        (tenderBid: TenderBid) => {
          const reasonCannotEdit = this.getReasonCannotEdit(tenderBid);

          if (reasonCannotEdit !== null) {
            return reasonCannotEdit;
          } else {
            return tenderBid.tender.getReasonCannotCreateOrderByCurrentUser();
          }
        },
        Core.currentPartner.isSeller,
        true
      ),
      new ContextMenuItem(
        'Показать расхождения',
        'difference',
        (tenderBid: TenderBid) => {
          Core.dialogs.OrderOptionsComparisonDialog.show({
            tenderId:                          tenderBid.tender.id,
            showOnlyMineOrderOptionAsSeller: Core.currentPartner!.isSeller,
          });
        },
        (tenderBid: TenderBid) => this.getReasonCannotCompareWithDemand(tenderBid),
        false,
        false,
      ),
      new ContextMenuItem(
        'Открыть партнера',
        'person',
        (tenderBid: TenderBid) => { PartnersService.showPartnerCard(tenderBid.seller.id); },
        () => this.getReasonCannotShowPartner(),
        false,  // hidden
        false,  // refreshAfter — просмотровая операция
      ),
      new ContextMenuItem(
        'История',
        'history',
        (tenderBid: TenderBid) => {
          Core.dialogs.FeedDialog.show({
            entityTypeName: 'tenderBid',
            entityId:       tenderBid.id,
            showAddComment: false,
          });
        },
        (tenderBid: TenderBid) => this.getReasonCannotEdit(tenderBid),
        false,  // hidden
        false,  // refreshAfter — просмотровая операция
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (tenderBid: TenderBid) => { return this.deleteBidWithConfirm(tenderBid); },
        (tenderBid: TenderBid) => this.getReasonCannotDelete(tenderBid),
      ),
    ];
  }
}

export default TenderBidsService;
